// Baked interior lighting (pure, no Three.js). Each room is lit by its own ceiling fixtures (warm,
// mostly downward) and by daylight through its window openings (cool, entering inward), plus a small
// bounce term. Only a room's own sources light it, so walls separate rooms without ray casting.
// Irradiance is in "scene units": interiors are shown with MeshBasicMaterial, so the baked value
// multiplies the albedo directly before tone mapping.

export type RGB = [number, number, number];

export interface RoomSources {
  /** Fixture positions (cm, generation space). */
  fixtures: [number, number, number][];
  /** Window openings: p1, p2 (cm), bottom/top z (cm) and the outward unit normal. */
  windows: { x1: number; y1: number; x2: number; y2: number; z0: number; z1: number; nx: number; ny: number }[];
}

export const LIGHTING = {
  ambient: [0.16, 0.16, 0.17] as RGB,
  fixtureColor: [1.0, 0.93, 0.8] as RGB,
  /** Fixture power: irradiance at 1 m straight below ≈ fixturePower / 1.25. */
  fixturePower: 1.5,
  /** Share of a fixture's light emitted sideways/up (the rest follows the downward cosine). */
  fixtureOmni: 0.25,
  daylightColor: [0.82, 0.9, 1.0] as RGB,
  /** Daylight per m² of window sample. */
  daylightPower: 1.1,
  /** Spacing of window samples along the opening (cm). */
  windowSample: 150,
};

interface Sample {
  x: number;
  y: number;
  z: number;
  nx: number;
  ny: number;
  /** Area weight (m²). */
  a: number;
}

/** Precomputed window samples for fast repeated evaluation. */
export function windowSamples(src: RoomSources): Sample[] {
  const out: Sample[] = [];
  for (const w of src.windows) {
    const L = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
    const H = w.z1 - w.z0;
    if (L < 1 || H < 1) continue;
    const nu = Math.max(1, Math.round(L / LIGHTING.windowSample));
    const nv = Math.max(1, Math.round(H / LIGHTING.windowSample));
    const a = ((L / nu) * (H / nv)) / 1e4;
    for (let i = 0; i < nu; i++) {
      const t = (i + 0.5) / nu;
      for (let j = 0; j < nv; j++) {
        out.push({ x: w.x1 + (w.x2 - w.x1) * t, y: w.y1 + (w.y2 - w.y1) * t, z: w.z0 + (H * (j + 0.5)) / nv, nx: w.nx, ny: w.ny, a });
      }
    }
  }
  return out;
}

/**
 * Irradiance at point (x, y, z) cm with unit normal (nx, ny, nz), written into `out`.
 * Fixtures: inverse-square with a 0.7 m softening radius, n·l, and a downward emission lobe.
 * Windows: each sample is a small area light facing into the room (cosine at both ends).
 */
export function irradiance(src: RoomSources, samples: Sample[], x: number, y: number, z: number, nx: number, ny: number, nz: number, out: RGB): RGB {
  let r = LIGHTING.ambient[0];
  let g = LIGHTING.ambient[1];
  let b = LIGHTING.ambient[2];
  const fc = LIGHTING.fixtureColor;
  for (const f of src.fixtures) {
    const dx = (f[0] - x) / 100;
    const dy = (f[1] - y) / 100;
    const dz = (f[2] - z) / 100;
    const d2 = dx * dx + dy * dy + dz * dz;
    const d = Math.sqrt(d2) || 1;
    const ndl = (nx * dx + ny * dy + nz * dz) / d;
    if (ndl <= 0) continue;
    // Direction from the fixture to the point is −(dx, dy, dz); a downward panel emits along −z.
    const down = Math.max(0, dz / d);
    const e = (LIGHTING.fixturePower * ndl * (LIGHTING.fixtureOmni + (1 - LIGHTING.fixtureOmni) * down)) / (d2 + 0.5);
    r += fc[0] * e;
    g += fc[1] * e;
    b += fc[2] * e;
  }
  const dc = LIGHTING.daylightColor;
  for (const s of samples) {
    const dx = (s.x - x) / 100;
    const dy = (s.y - y) / 100;
    const dz = (s.z - z) / 100;
    const d2 = dx * dx + dy * dy + dz * dz;
    const d = Math.sqrt(d2) || 1;
    const ndl = (nx * dx + ny * dy + nz * dz) / d;
    if (ndl <= 0) continue;
    // Light leaves the window inward (−outward normal): cosine between that and the direction to p.
    const cw = (s.nx * dx + s.ny * dy) / d;
    if (cw <= 0) continue;
    const e = (LIGHTING.daylightPower * s.a * ndl * cw) / (d2 + 0.3);
    r += dc[0] * e;
    g += dc[1] * e;
    b += dc[2] * e;
  }
  out[0] = r;
  out[1] = g;
  out[2] = b;
  return out;
}

/** Converts between render space (m, y up, relative to a tile origin) and generation space (cm). */
export interface RenderFrame {
  ox: number;
  oy: number;
}

/**
 * Bake a floor mesh in render space: each room's vertex range [start, count) is lit by that room's
 * sources; vertices outside every range (structure) get the ambient term only. Returns new colours
 * (the base vertex colours times irradiance).
 */
export function bakeMesh(positions: Float32Array, normals: Float32Array, colors: Float32Array, ranges: [number, number][], rooms: RoomSources[], frame: RenderFrame): Float32Array {
  const out = new Float32Array(colors.length);
  const amb = LIGHTING.ambient;
  for (let i = 0; i < colors.length; i += 3) {
    out[i] = colors[i] * amb[0];
    out[i + 1] = colors[i + 1] * amb[1];
    out[i + 2] = colors[i + 2] * amb[2];
  }
  const e: RGB = [0, 0, 0];
  ranges.forEach(([start, count], ri) => {
    const src = rooms[ri];
    if (!src) return;
    const samples = windowSamples(src);
    for (let v = start; v < start + count; v++) {
      const o = v * 3;
      // Render (x, y, z) = ((gx − ox)/100, gz/100, −(gy − oy)/100); normals likewise without scaling.
      const gx = positions[o] * 100 + frame.ox;
      const gy = -positions[o + 2] * 100 + frame.oy;
      const gz = positions[o + 1] * 100;
      irradiance(src, samples, gx, gy, gz, normals[o], -normals[o + 2], normals[o + 1], e);
      out[o] = colors[o] * e[0];
      out[o + 1] = colors[o + 1] * e[1];
      out[o + 2] = colors[o + 2] * e[2];
    }
  });
  return out;
}
